import { cn } from '@invent/wl-ui-kit-next';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import { Braces, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';

/**
 * The small controls the three payload editors are built from.
 *
 * ## Why these exist at all
 *
 * `params`, `prefill` and `compute` are stored as jsonb and were authored as JSON text areas,
 * on the reasoning that their grammar lives on the server and a hand-built form would be a
 * second source of truth for it. That reasoning held while the audience was the person who
 * wrote the grammar. It does not hold for the research analysts who will own this screen, who
 * have no reason to know what a trailing comma costs them.
 *
 * The objection was still right about one thing, so these editors answer it rather than
 * ignore it: **every closed list comes from `GET /vocabulary`**, never from a constant here.
 * Formats, grid column inputs, prefill sources, compute functions and the `max_from` token are
 * all server-owned, so a value added there appears here without a front-end release, and a
 * select cannot offer something the publish validator would reject.
 *
 * ## What is deliberately not modelled
 *
 * Two members of the grammar are open-ended by design — a choice type's `variants` is a bare
 * `{"type":"object"}` and a grid column's `when.eq` is `{}`, meaning any JSON at all. A form
 * cannot present those without inventing a schema the server does not have, so they keep a
 * JSON box. {@link RawJsonField} is that box, and {@link UnknownKeysNotice} is what makes the
 * arrangement safe: anything a form does not recognise is reported rather than dropped.
 */

// ---------------------------------------------------------------------------
// Values
// ---------------------------------------------------------------------------

/** A plain JSON object, which is what all three payloads are at the top level. */
export type JsonObject = Record<string, unknown>;

export function asObject(value: unknown): Nullable<JsonObject> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as JsonObject)
    : null;
}

export function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Sets one member, and **removes it when it is empty**.
 *
 * Every one of these schemas sets `additionalProperties: false` and treats absence as the
 * default, so writing `{"min": null}` is not the same as leaving `min` out — the first is a
 * value the validator has to accept, the second is the default. Clearing a box has to produce
 * the second.
 */
export function withMember(source: Nullable<JsonObject>, key: string, value: unknown): JsonObject {
  const next = { ...(source ?? {}) };
  if (value === undefined || value === null || value === '') delete next[key];
  else next[key] = value;
  return next;
}

/** An object with nothing left in it is no payload at all, which is how a field is cleared. */
export function emptyToNull(value: JsonObject): Nullable<JsonObject> {
  return Object.keys(value).length === 0 ? null : value;
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

export function FieldRow({
  label,
  hint,
  children,
  className
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={cn('grid gap-1', className)}>
      <span className="text-sm font-medium">{label}</span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      {children}
    </label>
  );
}

/**
 * A number box that distinguishes "blank" from "zero".
 *
 * `decimals: 0` and `min: 0` are meaningful values in this grammar, so the usual
 * `Number(text) || undefined` idiom would silently drop them.
 */
export function NumberField({
  label,
  hint,
  value,
  onChange,
  disabled,
  integer = false,
  min,
  max
}: {
  label: string;
  hint?: React.ReactNode;
  value: unknown;
  onChange: (next: Nullable<number>) => void;
  disabled?: boolean;
  integer?: boolean;
  min?: number;
  max?: number;
}) {
  const shown = typeof value === 'number' ? String(value) : '';

  return (
    <FieldRow label={label} hint={hint}>
      <Input
        type="number"
        inputMode={integer ? 'numeric' : 'decimal'}
        step={integer ? 1 : 'any'}
        min={min}
        max={max}
        value={shown}
        disabled={disabled}
        onChange={(event) => {
          const text = event.target.value;
          if (text.trim() === '') {
            onChange(null);
            return;
          }
          const parsed = integer ? Number.parseInt(text, 10) : Number(text);
          onChange(Number.isFinite(parsed) ? parsed : null);
        }}
      />
    </FieldRow>
  );
}

export function TextField({
  label,
  hint,
  value,
  onChange,
  disabled,
  placeholder,
  mono = false
}: {
  label: string;
  hint?: React.ReactNode;
  value: unknown;
  onChange: (next: Nullable<string>) => void;
  disabled?: boolean;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <FieldRow label={label} hint={hint}>
      <Input
        value={asString(value)}
        disabled={disabled}
        placeholder={placeholder}
        className={mono ? 'font-mono text-xs' : undefined}
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
      />
    </FieldRow>
  );
}

const UNSET = '__unset__';

/**
 * A select over a closed vocabulary, with an explicit "not set" entry.
 *
 * Optional members need a way back to absent, and Radix reserves the empty string, so the
 * sentinel is spelled rather than blank.
 */
export function ChoiceField({
  label,
  hint,
  value,
  options,
  onChange,
  disabled,
  placeholder = 'Not set',
  allowUnset = true,
  className
}: {
  label: string;
  hint?: React.ReactNode;
  value: unknown;
  options: { value: string; label?: string; note?: string }[];
  onChange: (next: Nullable<string>) => void;
  disabled?: boolean;
  placeholder?: string;
  allowUnset?: boolean;
  className?: string;
}) {
  const current = asString(value);

  return (
    <FieldRow label={label} hint={hint} className={className}>
      <Select
        value={current === '' ? undefined : current}
        disabled={disabled}
        onValueChange={(next) => onChange(next === UNSET ? null : next)}
      >
        <SelectTrigger aria-label={label}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {allowUnset && <SelectItem value={UNSET}>{placeholder}</SelectItem>}
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label ?? option.value}
              {option.note && (
                <span className="ml-2 text-xs text-muted-foreground">{option.note}</span>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldRow>
  );
}

/**
 * Three states, because the schema has three.
 *
 * `user_can_add` and friends are typed `["boolean","null"]`, and absent is a fourth thing
 * again: the engine's default. A checkbox can express two of those four, so it would quietly
 * turn "inherit the default" into "false" the first time anyone touched the row.
 */
export function TriStateField({
  label,
  hint,
  value,
  onChange,
  disabled,
  allowNull = true
}: {
  label: string;
  hint?: React.ReactNode;
  value: unknown;
  onChange: (next: Nullable<boolean> | undefined) => void;
  disabled?: boolean;
  /** Off where the schema types the member `boolean` rather than `["boolean","null"]`. */
  allowNull?: boolean;
}) {
  const current = value === true ? 'yes' : value === false ? 'no' : value === null ? 'null' : UNSET;

  return (
    <FieldRow label={label} hint={hint}>
      <Select
        value={current}
        disabled={disabled}
        onValueChange={(next) =>
          onChange(next === 'yes' ? true : next === 'no' ? false : next === 'null' ? null : undefined)
        }
      >
        <SelectTrigger aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={UNSET}>Not set — use the default</SelectItem>
          <SelectItem value="yes">Yes</SelectItem>
          <SelectItem value="no">No</SelectItem>
          {allowNull && <SelectItem value="null">Explicitly null</SelectItem>}
        </SelectContent>
      </Select>
    </FieldRow>
  );
}

/** An editable list of short strings — a column's options, a multi-year offset list. */
export function StringListField({
  label,
  hint,
  values,
  onChange,
  disabled,
  placeholder,
  addLabel = 'Add'
}: {
  label: string;
  hint?: React.ReactNode;
  values: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  addLabel?: string;
}) {
  return (
    <div className="grid gap-1">
      <span className="text-sm font-medium">{label}</span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      <div className="grid gap-1">
        {values.map((entry, index) => (
          <div key={index} className="flex items-center gap-1">
            <Input
              value={entry}
              disabled={disabled}
              placeholder={placeholder}
              className="font-mono text-xs"
              aria-label={`${label} ${index + 1}`}
              onChange={(event) => {
                const next = [...values];
                next[index] = event.target.value;
                onChange(next);
              }}
            />
            {!disabled && (
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remove ${label} ${index + 1}`}
                onClick={() => onChange(values.filter((_, at) => at !== index))}
              >
                <Trash2 className="size-4" aria-hidden />
              </Button>
            )}
          </div>
        ))}
        {!disabled && (
          <Button
            variant="outline"
            size="sm"
            className="w-fit"
            onClick={() => onChange([...values, ''])}
          >
            <Plus className="size-4" aria-hidden /> {addLabel}
          </Button>
        )}
        {values.length === 0 && disabled && (
          <p className="text-xs text-muted-foreground">None.</p>
        )}
      </div>
    </div>
  );
}

/** The frame a repeated sub-object sits in: a numbered card with its own remove. */
export function RepeaterItem({
  title,
  onRemove,
  disabled,
  children
}: {
  title: string;
  onRemove: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-2 rounded-md border border-border p-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-muted-foreground">{title}</span>
        {!disabled && (
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto"
            aria-label={`Remove ${title}`}
            onClick={onRemove}
          >
            <Trash2 className="size-4" aria-hidden />
          </Button>
        )}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The escape hatch
// ---------------------------------------------------------------------------

/**
 * The JSON box, kept for the two members the grammar leaves open and for anything a form
 * cannot yet express.
 *
 * It parses on every keystroke so that an invalid document never reaches the caller's state —
 * the page's save path can then trust that what it holds is a value, not a string that might
 * be one.
 */
export function RawJsonField({
  label,
  hint,
  value,
  onChange,
  disabled,
  rows = 6
}: {
  label: string;
  hint?: React.ReactNode;
  value: unknown;
  onChange: (next: unknown) => void;
  disabled?: boolean;
  rows?: number;
}) {
  const serialized = React.useMemo(
    () => (value === null || value === undefined ? '' : JSON.stringify(value, null, 2)),
    [value]
  );

  const [text, setText] = React.useState(serialized);
  const [error, setError] = React.useState<Nullable<string>>(null);

  // Re-seed when the value changes underneath — switching question, or a form control
  // rewriting the same payload — but never while the box holds text that does not parse,
  // which would throw away what is being typed.
  React.useEffect(() => {
    setText((current) => {
      try {
        const parsedCurrent = current.trim() === '' ? null : JSON.parse(current);
        return JSON.stringify(parsedCurrent) === JSON.stringify(value ?? null) ? current : serialized;
      } catch {
        return current;
      }
    });
  }, [serialized, value]);

  return (
    <div className="grid gap-1">
      <span className="text-sm font-medium">{label}</span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      <Textarea
        rows={rows}
        value={text}
        disabled={disabled}
        className="font-mono text-xs"
        placeholder="null"
        aria-label={label}
        onChange={(event) => {
          const next = event.target.value;
          setText(next);
          if (next.trim() === '') {
            setError(null);
            onChange(null);
            return;
          }
          try {
            onChange(JSON.parse(next));
            setError(null);
          } catch (parseError) {
            setError((parseError as Error).message);
          }
        }}
      />
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

/**
 * The guard that makes a partial form safe.
 *
 * These payloads are live definitions written before this screen existed, and a form that
 * rendered only the members it knows would drop the rest on the next save without saying so.
 * So every editor declares the members it handles, and anything else is named here with the
 * JSON view one click away. Nothing is removed — the unknown members stay on the value and
 * travel back to the server untouched.
 */
export function UnknownKeysNotice({
  keys,
  onOpenJson
}: {
  keys: string[];
  onOpenJson: () => void;
}) {
  if (keys.length === 0) return null;

  return (
    <Alert>
      <AlertTitle>This payload has {keys.length === 1 ? 'a member' : 'members'} the form does not show</AlertTitle>
      <AlertDescription className="grid gap-2">
        <span className="flex flex-wrap gap-1">
          {keys.map((key) => (
            <Badge key={key} variant="outline" className="font-mono text-[11px]">
              {key}
            </Badge>
          ))}
        </span>
        <span>
          {keys.length === 1 ? 'It is' : 'They are'} kept exactly as {keys.length === 1 ? 'it is' : 'they are'} and
          saved unchanged. Open the JSON view to read or edit {keys.length === 1 ? 'it' : 'them'}.
        </span>
        <Button variant="outline" size="sm" className="w-fit" onClick={onOpenJson}>
          <Braces className="size-4" aria-hidden /> Edit as JSON
        </Button>
      </AlertDescription>
    </Alert>
  );
}

/** The header every payload editor carries: what it is, and the way out to raw JSON. */
export function PayloadHeader({
  label,
  description,
  json,
  onJson,
  disabled
}: {
  label: string;
  description?: React.ReactNode;
  json: boolean;
  onJson: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold">{label}</h4>
        <Button
          variant={json ? 'default' : 'ghost'}
          size="sm"
          className="ml-auto"
          aria-pressed={json}
          disabled={disabled}
          onClick={() => onJson(!json)}
        >
          <Braces className="size-4" aria-hidden /> JSON
        </Button>
      </div>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
  );
}
